import {
  Button,
  ButtonVariants,
  IError,
  Modal,
  ModalVariants,
  TextField,
  useToast,
} from '@comptia-sso/core';
import { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

// Hooks.
import {
  useAuthLogout,
  useCoppaInstructors,
  useResendConsent,
  useSendConsent,
  useSetConsentAdult,
} from 'hooks';

// Components.
import { InstructorSelect } from './InstructorSelect';

// Styles.
import styles from './ConsentManagementPrompt.module.scss';

// Links surfaced within the guardian consent messaging, matching the B2C
// `injectPrivoLinksAndFormats` utility.
const PRIVO_LINK = 'https://www.privo.com/trusted-proven';
const INSTRUCTOR_LINK =
  'https://help.comptia.org/hc/en-us/articles/49958354362900-PRIVO-Information-for-Instructors';

interface IConsentManagementPromptProps {
  open: boolean;
  parentEmail?: string;
  consentStatus?: string;
  profileId: string;
  userEmail: string;
}

type Step = 'age' | 'guardian' | 'pending';

// Ages 1-120, matching the age dropdown in the Privo custom policy.
const AGES = Array.from({ length: 120 }, (_, index) => index + 1);

const ADULT_AGE = 18;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ConsentManagementPrompt = ({
  open,
  parentEmail: initialParentEmail,
  consentStatus,
  profileId,
  userEmail,
}: IConsentManagementPromptProps): ReactElement => {
  const { t } = useTranslation();
  const [popToast] = useToast();
  const logout = useAuthLogout();

  const [sendConsentAsync, { isLoading: isSending }] =
    useSendConsent(profileId);
  const [resendConsentAsync, { isLoading: isResending }] =
    useResendConsent(profileId);
  const [setConsentAdultAsync, { isLoading: isSettingAdult }] =
    useSetConsentAdult(profileId);
  const [instructors] = useCoppaInstructors(profileId, open);

  const isLoading = isSending || isResending || isSettingAdult;

  const statusLower = consentStatus?.toLowerCase();
  const isWaitingForConsent =
    statusLower === 'pending' || statusLower === 'revoked';

  const initialStep: Step = isWaitingForConsent ? 'pending' : 'age';

  const [step, setStep] = useState<Step>(initialStep);
  const [age, setAge] = useState<string>('');
  const [guardianEmail, setGuardianEmail] = useState<string>(
    initialParentEmail || '',
  );
  const [hasSent, setHasSent] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Sync step when consentStatus prop changes (e.g. profile loads after mount).
  useEffect(() => {
    const status = consentStatus?.toLowerCase();
    if (status === 'pending' || status === 'expired' || status === 'revoked') {
      setStep('pending');
      if (initialParentEmail) {
        setGuardianEmail(initialParentEmail);
      }
    }
  }, [consentStatus, initialParentEmail]);

  const isAdult = useMemo(() => {
    if (age === '') return false;
    return parseInt(age, 10) >= ADULT_AGE;
  }, [age]);

  const handleAgeSubmit = async () => {
    if (age === '') return;

    try {
      if (isAdult) {
        await setConsentAdultAsync();
        window.location.reload();
      } else {
        setStep('guardian');
      }
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    }
  };

  const isGuardianEmailSameAsUser =
    guardianEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();

  const isGuardianEmailInvalid =
    guardianEmail.trim() !== '' && !EMAIL_REGEX.test(guardianEmail.trim());

  const handleGuardianSubmit = async () => {
    if (!guardianEmail || isGuardianEmailSameAsUser || isGuardianEmailInvalid)
      return;

    try {
      await sendConsentAsync({
        age: age,
        parentEmail: guardianEmail,
      });
      setHasSent(true);
      setStep('pending');
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    }
  };

  const handleResend = async () => {
    if (!guardianEmail || isGuardianEmailSameAsUser || isGuardianEmailInvalid)
      return;

    try {
      const response = await resendConsentAsync({
        parentEmail: guardianEmail,
      });
      if (response.hitLimit) {
        popToast(t('ConsentManagement.ResendLimitReached'));
      } else {
        setHasSent(true);
      }
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    }
  };

  const guardianEmailError = isGuardianEmailSameAsUser
    ? t('ConsentManagement.SameEmailError')
    : isGuardianEmailInvalid
    ? t('ConsentManagement.InvalidEmailError')
    : undefined;

  const getTitle = (): string => {
    switch (step) {
      case 'age':
        return t('ConsentManagement.Title');
      case 'guardian':
        return t('ConsentManagement.GuardianTitle');
      case 'pending':
        return t('ConsentManagement.PendingTitle');
    }
  };

  return (
    <Modal
      disableClose
      open={open}
      title={getTitle()}
      variant={ModalVariants.Small}
    >
      {step === 'age' && (
        <div className={styles.content}>
          <p>{t('ConsentManagement.AgeDescription')}</p>
          <p>{t('ConsentManagement.AgeNote')}</p>
          <TextField
            label={t('ConsentManagement.AgeLabel')}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setAge(e.target.value)
            }
            select
            value={age}
          >
            {AGES.map((value) => (
              <option key={value} value={value.toString()}>
                {value}
              </option>
            ))}
          </TextField>
          <Button
            className={styles.fullWidth}
            disabled={isLoading || age === ''}
            onClick={handleAgeSubmit}
          >
            {t('ConsentManagement.SubmitAge')}
          </Button>
        </div>
      )}

      {step === 'guardian' && (
        <div className={styles.content}>
          <p>
            <Trans
              i18nKey="ConsentManagement.GuardianDescription"
              components={{
                privolink: (
                  <a
                    href={PRIVO_LINK}
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
              }}
            />
          </p>
          <p>
            <Trans
              i18nKey="ConsentManagement.GuardianInstructorNote"
              components={{
                instructorlink: (
                  <a
                    href={INSTRUCTOR_LINK}
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
              }}
            />
          </p>
          <p>{t('ConsentManagement.GuardianDeclineNote')}</p>
          <InstructorSelect
            error={guardianEmailError}
            instructors={instructors}
            label={t('ConsentManagement.GuardianEmailLabel')}
            onChange={setGuardianEmail}
            placeholder={t('ConsentManagement.SelectInstructor')}
            value={guardianEmail}
          />
          <Button
            className={styles.fullWidth}
            disabled={
              isLoading ||
              !guardianEmail ||
              isGuardianEmailSameAsUser ||
              isGuardianEmailInvalid
            }
            onClick={handleGuardianSubmit}
          >
            {t('ConsentManagement.SubmitGuardian')}
          </Button>
        </div>
      )}

      {step === 'pending' && (
        <div className={styles.content}>
          {hasSent && <p>{t('ConsentManagement.SentConfirmation')}</p>}
          {!hasSent && <p>{t('ConsentManagement.PendingDescription')}</p>}
          {!hasSent && (
            <>
              <InstructorSelect
                error={guardianEmailError}
                instructors={instructors}
                label={t('ConsentManagement.GuardianEmailLabel')}
                onChange={setGuardianEmail}
                placeholder={t('ConsentManagement.SelectInstructor')}
                value={guardianEmail}
              />
              <Button
                className={styles.fullWidth}
                disabled={
                  isLoading ||
                  !guardianEmail ||
                  isGuardianEmailSameAsUser ||
                  isGuardianEmailInvalid
                }
                onClick={handleResend}
              >
                {t('ConsentManagement.ResendButton')}
              </Button>
            </>
          )}
          <Button
            className={styles.fullWidth}
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true);
              window.location.reload();
            }}
            variant={ButtonVariants.Secondary}
          >
            {t('ConsentManagement.RefreshStatus')}
          </Button>
          <Button
            className={styles.fullWidth}
            onClick={() => logout()}
            variant={ButtonVariants.Text}
          >
            {t('ConsentManagement.SignOut')}
          </Button>
        </div>
      )}
    </Modal>
  );
};
